import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle2, Info, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { suppliersService, type Supplier } from '@/services/suppliersService';
import {
  supplyAgentService,
  type SupplyAgentLogisticsMasterStatus,
  type SupplyAgentLogisticsUploadResponse,
} from '@/services/supplyAgentService';
import { SupplyAgentLogisticsMasterEditor } from '@/modules/supply/components/SupplyAgentLogisticsMasterEditor';
import { SupplierSearchSelect } from './SupplierSearchSelect';

const btnSecondaryClass =
  'inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50';
const btnGhostClass =
  'inline-flex items-center gap-1.5 text-sm font-medium text-stocker-primary hover:underline';

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object' && 'message' in e) {
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string' && msg.trim()) return msg;
  }
  return String(e);
}

function formatUpdatedAt(iso: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Maestros logísticos por producto (cadena caja → base → pallet, dimensiones del pallet, tara, carga máxima),
 * reglas y contenedores por proveedor, y subida del Excel de maestros. Vive en Configuración junto al
 * catálogo de plantillas de camión y contenedor.
 */
export function LogisticsMastersPage() {
  const { t, i18n } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [searchParams] = useSearchParams();
  const [supplierId, setSupplierId] = useState<string>(() => {
    const fromUrl = searchParams.get('supplier') || '';
    return /^\d+$/.test(fromUrl) ? fromUrl : '';
  });
  const [masterStatus, setMasterStatus] = useState<SupplyAgentLogisticsMasterStatus | null>(null);
  const [masterLoading, setMasterLoading] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<SupplyAgentLogisticsUploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedSupplierId = supplierId ? Number(supplierId) : null;

  // Búsqueda en el servidor (una página de resultados), no el listado completo de proveedores.
  const searchSuppliers = useCallback((query: string) => {
    void suppliersService
      .searchSuppliers(query)
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, []);

  useEffect(() => {
    searchSuppliers('');
  }, [searchSuppliers]);

  // El proveedor elegido (p. ej. por enlace) se pide por id para mostrar su nombre aunque no salga en la búsqueda.
  useEffect(() => {
    if (!supplierId) {
      setSelectedSupplier(null);
      return;
    }
    let cancelled = false;
    void suppliersService.getSupplier(supplierId).then((found) => {
      if (!cancelled) setSelectedSupplier(found);
    });
    return () => {
      cancelled = true;
    };
  }, [supplierId]);

  const supplierOptions = selectedSupplier && !suppliers.some((s) => String(s.id) === String(selectedSupplier.id))
    ? [selectedSupplier, ...suppliers]
    : suppliers;

  const refreshMasterStatus = useCallback(async (sid: number | null) => {
    if (sid == null) {
      setMasterStatus(null);
      return;
    }
    setMasterLoading(true);
    try {
      setMasterStatus(await supplyAgentService.logisticsMasterStatus({ supplier_id: sid }));
    } catch {
      setMasterStatus(null);
    } finally {
      setMasterLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshMasterStatus(selectedSupplierId);
  }, [selectedSupplierId, refreshMasterStatus]);

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      setUploadResult(await supplyAgentService.uploadLogisticsExcel(file, { replace_existing: replaceExisting }));
      await refreshMasterStatus(selectedSupplierId);
    } catch (e) {
      setUploadResult(null);
      setError(errorMessage(e) || t('supply.supplyAgent.logisticsUploadError'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const productErrors = uploadResult?.products?.errors?.length || 0;
  const ruleErrors = uploadResult?.rules?.errors?.length || 0;
  const containerErrors = uploadResult?.containers?.errors?.length || 0;
  const hasUploadErrors = productErrors > 0 || ruleErrors > 0 || containerErrors > 0;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      <div>
        <Link to="/settings/logistics" className={btnGhostClass}>
          <ArrowLeft className="h-4 w-4" />
          {t('logisticsCatalog.masters.backToCatalog')}
        </Link>
        <h1 className="mt-2 text-xl font-semibold text-gray-900">{t('logisticsCatalog.masters.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('logisticsCatalog.masters.description')}</p>
      </div>

      {error ? <p className="text-sm text-red-600" role="alert">{error}</p> : null}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('logisticsCatalog.masters.uploadTitle')}</CardTitle>
          <CardDescription>{t('logisticsCatalog.masters.uploadHelp')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={replaceExisting}
              onChange={(e) => setReplaceExisting(e.target.checked)}
              className="rounded border-gray-300"
            />
            {t('supply.supplyAgent.logisticsReplaceExisting')}
          </label>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xlsm"
              className="hidden"
              onChange={(e) => void onPickFile(e.target.files?.[0] || null)}
            />
            <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className={btnSecondaryClass}>
              <Upload className="h-4 w-4" />
              {uploading ? t('supply.supplyAgent.logisticsUploading') : t('supply.supplyAgent.logisticsUpload')}
            </button>
          </div>
          {uploadResult ? (
            <div
              role="status"
              className={`rounded-md border px-3 py-2 text-sm ${hasUploadErrors ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}
            >
              <p className="font-medium">{t('supply.supplyAgent.logisticsUploadSummary')}</p>
              <ul className="mt-1 space-y-0.5 text-xs">
                <li>
                  {t('supply.supplyAgent.logisticsProductsCount', {
                    created: uploadResult.products?.created || 0,
                    updated: uploadResult.products?.updated || 0,
                    errors: productErrors,
                  })}
                </li>
                <li>
                  {t('supply.supplyAgent.logisticsRulesCount', {
                    created: uploadResult.rules?.created || 0,
                    updated: uploadResult.rules?.updated || 0,
                    errors: ruleErrors,
                  })}
                </li>
                <li>
                  {t('supply.supplyAgent.logisticsContainersCount', {
                    created: uploadResult.containers?.created || 0,
                    updated: uploadResult.containers?.updated || 0,
                    errors: containerErrors,
                  })}
                </li>
              </ul>
              {hasUploadErrors ? <p className="mt-1 text-xs">{t('supply.supplyAgent.logisticsUploadHasErrors')}</p> : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('logisticsCatalog.masters.editTitle')}</CardTitle>
          <CardDescription>{t('logisticsCatalog.masters.editHelp')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <SupplierSearchSelect
            suppliers={supplierOptions}
            onSearch={searchSuppliers}
            value={supplierId}
            onChange={setSupplierId}
            placeholder={t('logisticsCatalog.selectSupplier')}
            searchPlaceholder={t('logisticsCatalog.searchSupplier')}
            className="max-w-md"
          />
          {selectedSupplierId == null ? (
            <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{t('logisticsCatalog.masters.pickSupplier')}</p>
            </div>
          ) : masterLoading ? (
            <p className="text-sm text-gray-500">{t('supply.supplyAgent.logisticsMasterLoading')}</p>
          ) : masterStatus?.loaded ? (
            <>
              <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-medium">{t('supply.supplyAgent.logisticsMasterLoadedTitle')}</p>
                  {masterStatus.last_updated ? (
                    <p className="text-xs text-emerald-800/80">
                      {t('supply.supplyAgent.logisticsMasterUpdatedAt', {
                        date: formatUpdatedAt(masterStatus.last_updated, i18n.language),
                      })}
                    </p>
                  ) : null}
                </div>
              </div>
              <SupplyAgentLogisticsMasterEditor
                supplierId={selectedSupplierId}
                onChanged={() => void refreshMasterStatus(selectedSupplierId)}
              />
            </>
          ) : (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">{t('supply.supplyAgent.logisticsMasterEmptyTitle')}</p>
                <p className="text-xs">{t('supply.supplyAgent.logisticsMasterEmptyHint')}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
